// Facilities tab — every hospital, practice, office and referrer on record,
// with live counts of the physicians and clients linked to each.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../DataTable';
import { listFacilities, listFaxRoutes, faxRouteIndex, markVerified, facilityAddress, digits, fmtPhone, FACILITY_TYPES, FACILITY_TYPE_LABEL } from '../../lib/directory';
import { RefCode, Pill, VerifiedPill, Phone, Chip } from './ui';
import FacilityForm from './FacilityForm';

const TYPE_TONE = { hospital: 'info', specialty_clinic: 'neutral', pediatric_practice: 'neutral', referrer: 'copper', other: 'neutral' };

export default function FacilitiesTab({ canEdit }) {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);   // { facility } | { new: true }
  const [routes, setRoutes] = useState(() => new Map());

  const load = () => {
    listFaxRoutes().then(({ data }) => setRoutes(faxRouteIndex(data)));
    return listFacilities().then(({ data }) => { setRows(data || []); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase(); const d = digits(s);
    return rows.filter(f =>
      (showInactive || f.status === 'active') &&
      (type === 'all' || f.facility_type === type || (type === 'needs_check' && !f.verified_at)) &&
      (!s || f.name.toLowerCase().includes(s) || (f.parent_name || '').toLowerCase().includes(s) || (f.city || '').toLowerCase().includes(s) || (f.ref_code || '').toLowerCase() === s
        || (d.length >= 4 && ((f.npi || '').includes(d) || (f.phone || '').includes(d) || (f.fax || '').includes(d)))));
  }, [rows, q, type, showInactive]);

  const columns = useMemo(() => [
    { accessorKey: 'ref_code', header: 'Ref', size: 70, cell: ({ getValue }) => <RefCode code={getValue()} />, meta: { mobile: 'primary', mono: true } },
    { accessorKey: 'name', header: 'Facility', size: 260, meta: { mobile: 'primary' },
      cell: ({ row }) => <div><div style={{ fontWeight: 500 }}>{row.original.name}</div>{row.original.parent_name && <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>part of {row.original.parent_name}</div>}</div> },
    { accessorKey: 'facility_type', header: 'Type', size: 130, cell: ({ getValue }) => <Pill tone={TYPE_TONE[getValue()] || 'neutral'}>{FACILITY_TYPE_LABEL[getValue()] || getValue()}</Pill> },
    { accessorKey: 'npi', header: 'NPI', size: 110, cell: ({ getValue }) => getValue() || <span style={{ color: 'var(--text3)' }}>—</span>, meta: { mono: true } },
    { id: 'address', header: 'Address', size: 240, accessorFn: r => facilityAddress(r), meta: { truncate: true } },
    { accessorKey: 'phone', header: 'Phone', size: 130, cell: ({ getValue }) => <Phone value={getValue()} /> },
    // A site with no fax of its own is sent its parent's (directory_fax_routes).
    { accessorKey: 'fax', header: 'Fax', size: 150, cell: ({ row }) => {
      const f = row.original, r = routes.get(`facility:${f.id}`);
      if (f.fax || !r?.fax) return <Phone value={f.fax} />;
      return <span title={`No fax of its own — sent ${r.organisation_name}'s`} style={{ fontFamily: 'var(--mono)', fontSize: 12.5, whiteSpace: 'nowrap', color: 'var(--text3)' }}>{fmtPhone(r.fax)} ↑</span>;
    } },
    { accessorKey: 'physician_count', header: 'Physicians', size: 90, meta: { align: 'right' } },
    { accessorKey: 'client_count', header: 'Clients', size: 80, meta: { align: 'right' } },
    { accessorKey: 'verified_at', header: 'Verified', size: 120, cell: ({ getValue }) => <VerifiedPill verified_at={getValue()} /> },
  ], [routes]);

  const onSaved = () => { setModal(null); load(); };
  const verify = async (f) => { await markVerified('facilities', f.id, profile?.full_name || 'staff'); load(); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="form-input" style={{ flex: 1, minWidth: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, NPI, city, phone or fax…" />
        <Chip on={type === 'all'} onClick={() => setType('all')}>All</Chip>
        {FACILITY_TYPES.map(t => <Chip key={t.value} on={type === t.value} onClick={() => setType(t.value)}>{t.label}</Chip>)}
        <Chip on={type === 'needs_check'} onClick={() => setType('needs_check')}>Needs check <b>{rows.filter(f => f.status === 'active' && !f.verified_at).length}</b></Chip>
        <label style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> inactive</label>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setModal({ new: true })}>+ Add Facility</button>}
      </div>
      <DataTable tableId="directory-facilities" columns={columns} data={visible} getRowId={r => r.id} loading={loading}
        onRowClick={f => setModal({ facility: f })} maxHeight="calc(100vh - 300px)"
        rowStyle={f => f.status === 'inactive' ? { opacity: 0.55 } : undefined}
        emptyMessage={rows.length ? 'No facilities match.' : 'No facilities yet. Add the first one, or run the clean-up from the Review tab.'} />
      {modal?.new && <FacilityForm facilities={rows} onSaved={onSaved} onClose={() => setModal(null)} />}
      {modal?.facility && (
        <FacilityForm facility={modal.facility} facilities={rows} onSaved={onSaved} onClose={() => setModal(null)}
          onVerify={canEdit ? (f) => { verify(f); setModal(null); } : undefined} />
      )}
    </div>
  );
}
